import { Chip, KindDot, StateChip } from "../../../ds";
import type { ChangeKind } from "../../../ds";
import { ChangesetStates } from "../models/ChangeRequestDTO";
import { ReviewKind, ReviewOutcome } from "../utils/reviewModel";

export const kindLabels: Record<ReviewKind, string> = {
    created: "New",
    updated: "Edited",
    deleted: "Deleted",
    moved: "Moved",
    files: "Files",
};

export const outcomeLabels: Record<ReviewOutcome, string> = {
    applied: "Applied",
    failed: "Failed",
    skipped: "Not applied",
};

/** The review model's kinds in the design system's words (inventory "Change kinds"). */
const dsKinds: Record<ReviewKind, ChangeKind> = {
    created: "new",
    updated: "edited",
    deleted: "deleted",
    moved: "moved",
    files: "files",
};

/** Tree rows and the legend list the kinds in this order. */
export const kindOrder: ReviewKind[] = ["created", "updated", "deleted", "moved", "files"];

/** A small dot in the kind's colour (tree rows, summary chips); the kind is its accessible name. */
export function ReviewKindDot(props: { kind: ReviewKind }) {
    return <KindDot kind={dsKinds[props.kind]} label={kindLabels[props.kind]} />;
}

/** The kind as a chip (`New`, `Edited`…) next to the pane's title. */
export function ReviewKindTag(props: { kind: ReviewKind }) {
    return <KindDot kind={dsKinds[props.kind]} label={kindLabels[props.kind]} variant="chip" />;
}

/** "1 new", "2 edited": a neutral chip with the kind's dot, as the review header and the list summarise a change request. */
export function KindCountChip(props: { kind: ReviewKind; children: string }) {
    return (
        <Chip size="sm" icon={<KindDot kind={dsKinds[props.kind]} label={kindLabels[props.kind]} />}>
            {props.children}
        </Chip>
    );
}

const stateLabels: Record<string, string> = {
    [ChangesetStates.Proposed]: "Proposed",
    [ChangesetStates.Approved]: "Approved",
    [ChangesetStates.Applied]: "Applied",
    [ChangesetStates.Rejected]: "Rejected",
    [ChangesetStates.Failed]: "Failed",
    [ChangesetStates.Withdrawn]: "Withdrawn",
};

export const stateLabel = (state: string) => stateLabels[state] ?? state;

/** A change request's state as the inventory tones it (Proposed amber, Approved/Applied green, Rejected/Failed red, Withdrawn grey). */
export function ChangeRequestStateChip(props: { state: string; size?: "sm" | "md" }) {
    return <StateChip state={stateLabel(props.state)} size={props.size ?? "sm"} />;
}
